import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Button,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerFile } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '60vw',
    height: '70vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const FileContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f0f0f0',
  padding: '32px',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
  },
}));

const FileIcon = styled(DescriptionIcon)(({ theme }) => ({
  fontSize: '120px',
  color: primaryColor,
  opacity: 0.7,
  marginBottom: '24px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '90px',
    marginBottom: '16px',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: '150px',
  padding: '12px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: '600',
  margin: '8px',
  '&.download-btn': {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  '&.open-btn': {
    backgroundColor: primaryColor,
    color: 'white',
    '&:hover': {
      backgroundColor: '#4a4a9a',
    },
  },
}));

const OtherViewer = React.forwardRef(({
  open,
  onClose,
  filename = '',
  eventId = null,
  filePath = ''
}, _ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [downloadRequested, setDownloadRequested] = useState(false);
  const {
    fileBlob,
    fileUrl,
    loading,
    progress,
    error,
  } = useViewerFile({
    open,
    eventId,
    filePath,
    enabled: downloadRequested,
    download: true,
    missingMessage: t('viewer.file_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setDownloadRequested(false);
    }
  }, [open]);

  const downloadFile = () => {
    if (fileBlob) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setDownloadRequested(true);
    }
  };

  useEffect(() => {
    if (!downloadRequested || !fileBlob || !fileUrl || loading) return;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = filename || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadRequested(false);
  }, [downloadRequested, fileBlob, fileUrl, filename, loading]);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Get file extension for display
  const getFileExtension = () => {
    if (!filename) return '';
    const extension = filename.split('.').pop();
    return extension ? extension.toUpperCase() : '';
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <DescriptionIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.document_viewer_title')} - ${filename}` : t('viewer.document_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadFile} 
            size="small"
            title={t('viewer.download_file')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.loading_file')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{
        "--dialog-content-top-padding": "0px",
        padding: 0, 
        height: '100%', 
        overflow: 'auto',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(87, 87, 176, 0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(87, 87, 176, 0.4)',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'rgba(87, 87, 176, 0.6)',
          },
        },
        // Firefox scrollbar styling
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
      }}>
        <FileContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 300, mb: 2 }}
              />
              <Typography color="text.secondary">
                {t('viewer.loading_file')} {Math.round(progress)}%
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_file')}
              </Typography>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Box>
          ) : (
            <>
              <FileIcon />
              <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 'bold' }}>
                {filename || 'Unknown File'}
              </Typography>
              {getFileExtension() && (
                <Typography variant="h6" gutterBottom sx={{ color: '#666', mb: 3 }}>
                  {getFileExtension()} File
                </Typography>
              )}
              <Typography variant="body1" sx={{ color: '#888', mb: 4, maxWidth: '400px' }}>
                This file format cannot be previewed in the browser. You can download the file.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <ActionButton 
                  className="download-btn"
                  variant="contained" 
                  startIcon={<DownloadIcon />}
                  onClick={downloadFile}
                >
                  {t('viewer.download_file')}
                </ActionButton>
              </Box>
            </>
          )}
        </FileContainer>
      </ViewerDialogContent>
    </StyledDialog>
  );
});

// Set display name for debugging
OtherViewer.displayName = 'OtherViewer';

export default OtherViewer;
